import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Typography, Container, Toolbar, Grid, Paper, List, ListItem, ListItemIcon, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  RocketLaunch,
  Groups,
  Handshake,
  TrendingUp,
  AccountBalance,
  EmojiEvents,
  Security,
  CheckCircle
} from "@mui/icons-material";
import routes from "../../../Config/routes";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import { globalStyles } from "../../../Utils/Styles";

const WhyPartner = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accentColor = theme.palette.mode === "light" ? "#0d47a1" : "#90caf9";
  const secondaryColor = "#4caf50";

  const handleAccess = () => navigate(`${routes.creatorConnectOnboard}`);

  const FeatureCard = ({ icon, title, content }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 2,
        border: "1px solid",
        borderColor: theme.palette.divider,
        background: theme.palette.background.paper,
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Box sx={{ color: accentColor, mb: 2 }}>{icon}</Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {content}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${accentColor} 100%)`,
          color: "#fff",
          textAlign: "center"
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant={isMobile ? "h4" : "h2"}
            gutterBottom
            sx={{ fontWeight: 800 }}
          >
            Elevate Your Creator Business
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 300 }}>
            Professional tools for modern creators
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={handleAccess}
            sx={{
              fontSize: "1.1rem",
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: theme.shadows[4]
            }}
          >
            Request Early Access
          </Button>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Value Proposition Cards */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={6} lg={4}>
            <FeatureCard
              icon={<RocketLaunch fontSize="large" />}
              title="Accelerate Growth"
              content="Streamline operations to focus on what matters most - creating exceptional content and building your brand."
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <FeatureCard
              icon={<TrendingUp fontSize="large" />}
              title="Maximize Earnings"
              content="Access premium partnerships and revenue streams with our curated brand network."
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <FeatureCard
              icon={<Security fontSize="large" />}
              title="Enterprise Security"
              content="Financial-grade protection for your data and transactions with bank-level encryption."
            />
          </Grid>
        </Grid>

        {/* Program Comparison */}
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Choose Your Path
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto", mb: 6 }}
          >
            Select the program that matches your growth stage and business needs
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  borderTop: `4px solid ${theme.palette.primary.main}`
                }}
              >
                <Box
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: "#fff",
                    display: "inline-block",
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    mb: 3
                  }}
                >
                  <Typography variant="h6">Associate Program</Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  Perfect for creators establishing their business foundation
                </Typography>
                <List>
                  {[
                    "Free platform access",
                    "Basic campaign tools",
                    "Standard payment processing",
                    "Self-service brand matching"
                  ].map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <Typography variant="body1">{item}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 3,
                  borderTop: `4px solid ${secondaryColor}`,
                  position: "relative",
                  overflow: "hidden"
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: secondaryColor,
                    color: "#fff",
                    px: 2,
                    py: 0.5,
                    borderBottomLeftRadius: 8,
                    fontWeight: 700
                  }}
                >
                  RECOMMENDED
                </Box>
                <Box
                  sx={{
                    bgcolor: secondaryColor,
                    color: "#fff",
                    display: "inline-block",
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    mb: 3
                  }}
                >
                  <Typography variant="h6">Partner Program</Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  For scaling creators ready for enterprise partnerships
                </Typography>
                <List>
                  {[
                    "Priority deal flow",
                    "Dedicated account manager",
                    "BlitzPay instant access",
                    "Premium brand introductions",
                    "Advanced analytics dashboard",
                    "Custom contract support"
                  ].map((item) => (
                    <ListItem key={item} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36, color: secondaryColor }}>
                        <CheckCircle />
                      </ListItemIcon>
                      <Typography variant="body1">{item}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Financial Section */}
        <Box
          sx={{
            mb: 8,
            p: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
            border: "1px solid",
            borderColor: theme.palette.divider
          }}
        >
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={6}>
              <AccountBalance
                fontSize="large"
                sx={{ color: accentColor, mb: 2, fontSize: 60 }}
              />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                BlitzPay Advantage
              </Typography>
              <Typography variant="body1" paragraph>
                Financial tools designed specifically for creator economy needs
              </Typography>
              <List>
                {[
                  "Instant payouts for all your brand deals",
                  "Automated invoicing & tax documentation",
                  "Multi-currency support",
                  "Real-time dealflow tracking",
                ].map((item) => (
                  <ListItem key={item} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36, color: secondaryColor }}>
                      <CheckCircle />
                    </ListItemIcon>
                    <Typography variant="body1">{item}</Typography>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default
                }}
              >
                <Grid container spacing={2}>
                  {[
                    { value: "24h", label: "Average payout time" },
                    { value: "97%", label: "Payment success rate" },
                    { value: "5.2x", label: "ROI increase" },
                    { value: "3.8x", label: "Faster invoice clearance" }
                  ].map((stat, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, color: accentColor }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body2">{stat.label}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            p: 6,
            borderRadius: 3,
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            maxWidth: 800,
            mx: "auto"
          }}
        >
          <EmojiEvents
            fontSize="large"
            sx={{ color: theme.palette.warning.main, fontSize: 60, mb: 2 }}
          />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Transform Your Business?
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: "auto" }}
          >
            Join industry leaders who've increased their revenue by an average of
            142% in their first year
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleAccess}
            sx={{
              fontSize: "1.1rem",
              px: 6,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: `0 4px 20px ${theme.palette.primary.main}40`
            }}
          >
            Get Started Today
          </Button>
        </Box>
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default WhyPartner;